import React, { useState, useEffect } from 'react';
import CookieConsent from 'react-cookie-consent';
import { motion, AnimatePresence } from 'framer-motion';

const CookieConsentBanner = () => {
  const [showDetailed, setShowDetailed] = useState(false);
  const [consentPreferences, setConsentPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Load saved preferences from localStorage
    const saved = localStorage.getItem('cookie-consent-preferences');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConsentPreferences(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Error parsing saved cookie preferences:', e);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const newPreferences = {
      essential: true,
      analytics: true,
      marketing: true
    };
    setConsentPreferences(newPreferences);
    localStorage.setItem('cookie-consent-preferences', JSON.stringify(newPreferences));

    // Update Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted'
      });
    }
  };

  const handleAcceptEssential = () => {
    const newPreferences = {
      essential: true,
      analytics: false,
      marketing: false
    };
    setConsentPreferences(newPreferences);
    localStorage.setItem('cookie-consent-preferences', JSON.stringify(newPreferences));

    // Update Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
  };

  const handleCustomAccept = () => {
    localStorage.setItem('cookie-consent-preferences', JSON.stringify(consentPreferences));

    // Update Google Analytics consent based on preferences
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: consentPreferences.analytics ? 'granted' : 'denied',
        ad_storage: consentPreferences.marketing ? 'granted' : 'denied'
      });
    }
    setShowDetailed(false);
  };

  const handlePreferenceChange = (category, value) => {
    if (category === 'essential') return; // Essential cannot be changed
    setConsentPreferences(prev => ({ ...prev, [category]: value }));
  };

  return (
    <AnimatePresence>
      <CookieConsent
        location="bottom"
        buttonText="Accept All"
        declineButtonText="Essential Only"
        enableDeclineButton
        onAccept={handleAcceptAll}
        onDecline={handleAcceptEssential}
        cookieName="melbourne-floral-consent"
        style={{
          background: '#2B373B',
          color: '#fff',
          fontSize: '14px',
          padding: '20px',
          alignItems: 'center'
        }}
        buttonStyle={{
          background: '#4CAF50',
          color: '#fff',
          fontSize: '14px',
          padding: '10px 20px',
          borderRadius: '4px',
          margin: '0 10px'
        }}
        declineButtonStyle={{
          background: '#f44336',
          color: '#fff',
          fontSize: '14px',
          padding: '10px 20px',
          borderRadius: '4px'
        }}
        expires={365}
      >
        <div className="flex flex-col space-y-4">
          <div>
            <strong>We use cookies to enhance your experience.</strong>
            <br />
            By continuing to visit this site you agree to our use of cookies.
          </div>

          {!showDetailed ? (
            <button
              onClick={() => setShowDetailed(true)}
              className="text-blue-400 hover:text-blue-300 underline text-sm self-start"
            >
              Manage Preferences
            </button>
          ) : (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3"
            >
              <div className="text-sm">
                <h4 className="font-semibold mb-2">Cookie Preferences</h4>

                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={consentPreferences.essential}
                      disabled
                      className="rounded"
                    />
                    <span>Essential Cookies (Required)</span>
                    <span className="text-xs text-gray-400">Always enabled</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={consentPreferences.analytics}
                      onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                      className="rounded"
                    />
                    <span>Analytics Cookies</span>
                    <span className="text-xs text-gray-400">Help us improve our website</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={consentPreferences.marketing}
                      onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                      className="rounded"
                    />
                    <span>Marketing Cookies</span>
                    <span className="text-xs text-gray-400">Used for advertising</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={handleCustomAccept}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm"
                >
                  Save Preferences
                </button>
                <button
                  onClick={() => setShowDetailed(false)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded text-sm"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </CookieConsent>
    </AnimatePresence>
  );
};

export default CookieConsentBanner;